class ProductGridItem extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.popup = null;
    this.popupBg = null;
  }

  connectedCallback() {
    // Ensure popup background exists (shared across all product grids)
    this.ensurePopupBackground();

    // Initialize this product grid
    this.init();
  }

  ensurePopupBackground() {
    // Check if popup background already exists
    this.popupBg = document.querySelector('.product-grid--options-bg');

    if (!this.popupBg) {
      this.popupBg = document.createElement('div');
      this.popupBg.className = 'product-grid--options-bg';
      document.body.appendChild(this.popupBg);
    }
  }

  init() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.popup = this.querySelector('.product-grid--options');

    // Setup variant selection
    this.setupVariantSelection();

    // Setup draggable popup for touch devices
    this.setupDraggablePopup();

    // Setup form submission
    this.setupFormSubmission();
  }

  setupVariantSelection() {
    const inputs = this.querySelectorAll('input');

    inputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const option = parseInt(e.target?.dataset.index?.replace('option', '')) || 0;
        if (option > 0) {
          this.selectVariant();
        }
      });
    });
  }

  selectVariant() {
    try {
      const productDataScript = this.querySelector('[data-product-variant-script]')?.innerHTML || '{}';
      const productData = JSON.parse(productDataScript);

      const selectedOptions = {};
      const optionFields = this.querySelectorAll('input[type=radio][data-index]:checked, input[type=hidden][data-index]');

      optionFields.forEach(option => {
        if (option.dataset.index?.includes('option')) {
          const optionIndex = parseInt(option.dataset.index.replace('option', ''));
          selectedOptions[optionIndex] = option.value;
        }
      });

      let variantId = productData.variants[0].id;
      let selectedVariant = productData.variants[0];

      if (productData.variants.length > 0) {
        const variantFound = productData.variants.find(variant => {
          return variant.options.every((option, index) => {
            return selectedOptions[index + 1] === option;
          });
        });

        if (variantFound) {
          variantId = variantFound.id;
          selectedVariant = variantFound;
        }
      }

      const selector = this.querySelector('[name="id"]');
      if (selector) {
        selector.value = variantId;
      }

      // Update button text with variant price
      this.updateButtonPrice(selectedVariant);
    } catch (e) {
      console.error('Error selecting variant:', e);
    }
  }

  updateButtonPrice(variant) {
    // Find ALL buttons with the add-to-cart-text attribute (popup button + main button)
    const buttonTextSpans = this.querySelectorAll('[data-add-to-cart-text]');
    if (!buttonTextSpans.length) return;

    const firstSpan = buttonTextSpans[0];
    const defaultText = firstSpan.dataset.defaultText || firstSpan.dataset.addToCartText;
    const preorderText = theme?.strings?.preorder || 'Pre-order';
    const soldOutText = theme?.strings?.soldOut || 'Sold Out';

    let buttonText = defaultText;
    let isSoldOut = false;
    let isPreorder = false;

    // Check if preorder (out of stock but can continue selling). A variant
    // whose shipping note means "ships now" is excluded: it stays sellable but
    // reads as a new release, so the button keeps the default Add to Cart text
    // and no Preorder property is written to the line.
    if (
      variant.inventory_quantity <= 0 &&
      variant.inventory_policy === 'continue' &&
      !variant.preorder_ships_now
    ) {
      buttonText = preorderText;
      isPreorder = true;
    } else if (!variant.available) {
      buttonText = soldOutText;
      isSoldOut = true;
    }

    // Format price
    const formattedPrice = theme?.Currency?.formatMoney
      ? theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)
      : this.formatMoney(variant.price);

    // Update ALL button text spans
    buttonTextSpans.forEach(span => {
      if (!isSoldOut) {
        span.textContent = buttonText + ' – ' + formattedPrice;
      } else {
        span.textContent = buttonText;
      }
    });

    // Update ALL button disabled states
    const buttons = this.querySelectorAll('[data-add-to-cart]');
    buttons.forEach(button => {
      button.disabled = isSoldOut;
      if (isSoldOut) {
        button.classList.add('btn--disabled');
      } else {
        button.classList.remove('btn--disabled');
      }
    });

    // Handle preorder property
    this.updatePreorderProperty(isPreorder, variant.preorder_note);
  }

  updatePreorderProperty(isPreorder, preorderNote) {
    // Find all forms in this product grid item
    const forms = this.querySelectorAll('form');

    forms.forEach(form => {
      // Find existing preorder property input
      let preorderInput = form.querySelector('[data-preorder-property]');

      if (isPreorder) {
        // Add or update preorder property
        if (!preorderInput) {
          preorderInput = document.createElement('input');
          preorderInput.type = 'hidden';
          preorderInput.name = 'properties[Preorder]';
          preorderInput.setAttribute('data-preorder-property', '');
          form.appendChild(preorderInput);
        }
        preorderInput.value = preorderNote || '';
      } else {
        // Remove preorder property if exists
        if (preorderInput) {
          preorderInput.remove();
        }
      }
    });
  }

  formatMoney(cents) {
    // Fallback money formatter if theme.Currency is not available
    return '$' + (cents / 100).toFixed(2);
  }

  setupDraggablePopup() {
    const draggable = this.querySelector('[data-close-options]');
    if (!draggable || !this.popup) return;

    draggable.addEventListener('touchstart', (e) => {
      const touchStart = e.touches[0].clientY;
      let touchDistance = 0;

      const touchMove = (e) => {
        touchDistance = e.touches[0].clientY - touchStart;
        e.stopPropagation();

        if (touchDistance > 0) {
          this.popup.style.transform = `translateY(${touchDistance}px)`;
        }
      };

      const touchEnd = (e) => {
        draggable.removeEventListener('touchmove', touchMove);
        draggable.removeEventListener('touchend', touchEnd);
        this.unlockBody();

        if (touchDistance > 20) {
          this.popup?.classList.remove('opened');
          this.popupBg.classList.remove('opened');

          setTimeout(() => {
            if (this.popup) {
              this.popup.style.transform = 'translateY(0)';
            }
          }, 300);
        } else {
          if (this.popup) {
            this.popup.style.transform = 'translateY(0)';
          }
        }
      };

      draggable.addEventListener('touchmove', touchMove);
      draggable.addEventListener('touchend', touchEnd);
    });
  }

  setupFormSubmission() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      if (this.popup && this.isTouchDevice() && !this.popup.classList.contains('opened')) {
        // Show popup on touch devices
        this.popup.classList.add('opened');
        this.popupBg.classList.add('opened');
        this.lockBody();
      } else {
        // Add to cart
        this.addToCart();
      }
    });
  }

  async addToCart() {
    const addToCartBtn = this.form.querySelector('button[type="submit"]');
    addToCartBtn?.classList.add('btn--loading');

    // Collect properties from the form
    const properties = {};
    const propertyInputs = this.form.querySelectorAll('[name^="properties["]');
    propertyInputs.forEach(input => {
      const match = input.name.match(/properties\[(.+?)\]/);
      if (match && input.value) {
        properties[match[1]] = input.value;
      }
    });

    const formData = {
      items: [{
        id: this.form.querySelector('[name="id"]').value,
        quantity: 1,
        ...(Object.keys(properties).length > 0 && { properties })
      }]
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      addToCartBtn?.classList.remove('btn--loading');
      this.popup?.classList.remove('opened');
      this.popupBg.classList.remove('opened');
      this.unlockBody();

      // Dispatch custom event
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data,
            addToCartBtn: addToCartBtn
          }
        })
      );
    } catch (error) {
      console.error('Error adding to cart:', error);
      addToCartBtn?.classList.remove('btn--loading');
    }
  }

  isTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
  }

  lockBody() {
    document.body.classList.add('locked');
  }

  unlockBody() {
    document.body.classList.remove('locked');
  }
}

customElements.define('product-grid-item', ProductGridItem);
